import { Component, computed, inject, signal } from '@angular/core';
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import { SeoService } from '../../shared/seo.service';
import { SEO_PAGES_CONFIG } from '../../shared/header/header.lists';
import { Pages } from '../../shared/types/common.enums';
import { NgbAccordionModule } from '@ng-bootstrap/ng-bootstrap';
import { DeliveriesService } from '../../settings/account/deliveries/deliveries.service';
import { tap } from 'rxjs';
import { AtlantaDeliverySchedule, MembershipsInfo } from '../../shared/types/common.types';
import { DomSanitizer } from '@angular/platform-browser';
import { ModalContentData } from '../../shared/modal-content/modal-content';
import { ModalContentService } from '../../shared/modal-content/modal-content.service';
import { ModalContentTypes } from '../../shared/constants/modal-content-types';
import { SignalsStoreService } from '../../shared/signals-store.service';

@Component({
  selector: 'app-atlanta-delivery-schedule',
  imports: [
    MatTabGroup,
    MatTab,
    NgbAccordionModule
  ],
  templateUrl: './atlanta-delivery-schedule.component.html',
  styleUrl: './atlanta-delivery-schedule.component.scss'
})
export class AtlantaDeliveryScheduleComponent {

  //#region Services

  #seoService = inject(SeoService);

  #deliveriesService = inject(DeliveriesService);

  #sanitizer = inject(DomSanitizer);

  #modalService = inject(ModalContentService);

  #signalStoreService = inject(SignalsStoreService);

  //#endregion

  //#region Properties

  memberships = signal<AtlantaDeliverySchedule[]>([]);

  isLoading = signal(true);

  hasSession = computed(() => this.#signalStoreService.hasSession());

  //#endregion

  //#region Constructor

  constructor() {
    this.#seoService
      .setUpSEOTags(SEO_PAGES_CONFIG[Pages.atlantaDeliverySchedule]);
    this.#getInfo()
  }

  //#endregion

  //#region Methods

  #getInfo() {

    this.#deliveriesService
      .getZonesData()
      .pipe(
        tap((resp: any) => resp && this.#setUpMemberships(resp))
      )
      .subscribe();
  }

  #setUpMemberships(memberships: MembershipsInfo) {

    const arr: AtlantaDeliverySchedule[] = [];

    for (const key in memberships) {
      if (memberships.hasOwnProperty(key)) {

        const membership = memberships[key];

        const isPickup = key.replace('Zones', '').toLowerCase() === 'pickup';

        arr.push({
          isPickup,
          name: isPickup ? 'Pick Up' : 'Home Delivery',
          subtitle: isPickup ?
            `<p>
              Would you prefer to pick up your order? Enjoy the convenience of our pop-up locations scattered across Atlanta, making it easy for you to grab your order at a location near you. Simply select one of the locations below to explore specific pickup times and additional details. Orders must be <strong>placed by 9 am the day before delivery</strong>.
           </p>` :
            `<p>
            Enjoy the convenience of home delivery with Carlton Farms. We deliver to many neighborhoods around the Atlanta metropolitan area on a bi-weekly basis. <strong>Place your order for $100 or more</strong>, and we’ll bring it straight to your door!
           </p>`,
          title: isPickup ? 'Delivery Time' : 'Weekly Delivery',
          map: isPickup ?
            this.#sanitizer.bypassSecurityTrustResourceUrl('https://www.google.com/maps/d/u/0/embed?mid=1yNjpzIO1A1ycR-OJcSFmuzCKphm1K1A&ehbc=2E312F&ll=34.00894980301681%2C-84.54967875&z=10') :
            this.#sanitizer.bypassSecurityTrustResourceUrl('https://www.google.com/maps/d/u/0/embed?mid=1p3bchq7wzOIaRyi-2_sZyzYWgpwO07U&ehbc=2E312F&ll=34.00894980301679%2C-84.54967875&z=9'),
          days: membership,
          img: isPickup ? 'pickup.webp' : 'home-delivery.webp'
        })
      }
    }

    this.memberships.set(arr.reverse());
    this.isLoading.set(false);
  }

  openModalCheckAddress() {

    let data: ModalContentData = {
      title: 'Choose your delivery preference',
      textContent:
        `Before you start shopping, let's make sure you're located near one of our delivery routes or pick up locations`,
      closeable: false,
    };

    this.#modalService.openModal(ModalContentTypes.CHECK_ADDRESS, data, undefined);
  }

  //#endregion
}
